import React, { useMemo, useState } from 'react';
import AppointmentChip from './AppointmentChip';
import { CAL } from '@/constants/testIds';

// Time grid: 08:00 → 21:00, 30-min slots
const START_HOUR = 8;
const END_HOUR = 21;
const HOUR_HEIGHT = 60; // px per hour
const DAY_MS = 24 * 60 * 60 * 1000;

const sameDay = (a, b) => {
  const x = new Date(a); const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
};
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7;
  return addDays(x, -dow);
};

function timeToOffset(dateStr, dayStart) {
  const t = new Date(dateStr);
  const diffMin = (t - dayStart) / 60000;
  const offsetMin = diffMin - START_HOUR * 60;
  return (offsetMin / 60) * HOUR_HEIGHT;
}

function durationHeight(apt) {
  const s = new Date(apt.start_ts);
  const e = new Date(apt.end_ts);
  return Math.max(24, ((e - s) / 60000 / 60) * HOUR_HEIGHT);
}

function TimeGutter() {
  const hours = [];
  for (let h = START_HOUR; h < END_HOUR; h++) hours.push(h);
  return (
    <div className="w-14 shrink-0 border-r border-stone-200 bg-white sticky left-0 z-10">
      <div className="h-8 border-b border-stone-200 sticky top-0 bg-white z-10" />
      {hours.map((h) => (
        <div
          key={h}
          className="border-b border-stone-100 text-[10px] text-stone-500 font-medium pr-2 text-right pt-1 tabular-nums"
          style={{ height: HOUR_HEIGHT }}
        >
          {String(h).padStart(2, '0')}:00
        </div>
      ))}
    </div>
  );
}

function DayColumn({ date, columnKey, appointments, lookups, onSelect, onDropAppointment, header, colorTag }) {
  const [dragOverHour, setDragOverHour] = useState(null);
  const dayStart = startOfDay(date);
  const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;
  const isToday = sameDay(date, new Date());

  const hours = [];
  for (let h = START_HOUR; h < END_HOUR; h++) hours.push(h);

  const handleDrop = (e, hour) => {
    e.preventDefault();
    const aptId = e.dataTransfer.getData('text/plain');
    if (!aptId) return;
    // find appointment being dragged to preserve duration
    const apt = appointments.find((a) => a.id === aptId);
    // if not in this column's list, fall back to same duration = 30
    const durMin = apt ? Math.max(15, (new Date(apt.end_ts) - new Date(apt.start_ts)) / 60000) : 30;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    // snap to 15-min
    const snappedMin = Math.round(offsetY / (HOUR_HEIGHT / 4)) * 15;
    const newStart = new Date(dayStart);
    newStart.setMinutes(hour * 60 + snappedMin);
    const newEnd = new Date(newStart.getTime() + durMin * 60000);
    setDragOverHour(null);
    onDropAppointment && onDropAppointment(aptId, newStart, newEnd);
  };

  return (
    <div className="flex-1 min-w-[140px] border-r border-stone-200 last:border-r-0 flex flex-col">
      <div
        className={`h-8 border-b border-stone-200 flex items-center justify-center text-xs font-semibold uppercase tracking-wider sticky top-0 z-[5] ${
          isToday ? 'bg-stone-900 text-white' : 'bg-white text-stone-600'
        }`}
        style={colorTag ? { borderTop: `3px solid ${colorTag}` } : {}}
      >
        {header}
      </div>
      <div className="relative" style={{ height: totalHeight }}>
        {hours.map((h) => (
          <div
            key={h}
            data-testid={CAL.timeSlot(columnKey, h)}
            className={`hc-time-slot border-b border-stone-100 ${dragOverHour === h ? 'hc-drop-target' : ''}`}
            style={{ height: HOUR_HEIGHT }}
            onDragOver={(e) => { e.preventDefault(); setDragOverHour(h); }}
            onDragLeave={() => setDragOverHour(null)}
            onDrop={(e) => handleDrop(e, h)}
          />
        ))}
        {appointments.map((apt) => (
          <div
            key={apt.id}
            className="absolute left-1 right-1"
            style={{
              top: timeToOffset(apt.start_ts, dayStart),
              height: durationHeight(apt),
            }}
          >
            <AppointmentChip
              apt={apt}
              patient={lookups.patient[apt.patient_id]}
              aptType={lookups.aptType[apt.appointment_type_id]}
              employee={lookups.employee[apt.audiologist_id]}
              onClick={onSelect}
              compact={durationHeight(apt) < 50}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CalendarCanvas({
  view, cursor, appointments, employees, lookups, loading,
  onSelectAppointment, onAppointmentMoved,
}) {
  const days = useMemo(() => {
    if (view === 'week' || view === 'resource') {
      const s = startOfWeek(cursor);
      return Array.from({ length: 7 }, (_, i) => addDays(s, i));
    }
    return [startOfDay(cursor)];
  }, [view, cursor]);

  const audios = useMemo(() => employees.filter(e => e.role === 'audiologist'), [employees]);

  // Group appointments per day
  const perDay = useMemo(() => {
    const m = new Map();
    days.forEach((d) => m.set(d.toDateString(), []));
    appointments.forEach((a) => {
      const key = new Date(a.start_ts).toDateString();
      if (m.has(key)) m.get(key).push(a);
    });
    return m;
  }, [appointments, days]);

  const perAudio = useMemo(() => {
    // Only for resource view + day mode: group by audiologist
    if (view !== 'resource') return null;
    const m = new Map();
    audios.forEach((e) => m.set(e.id, []));
    m.set('__unassigned__', []);
    appointments.forEach((a) => {
      const key = a.audiologist_id && m.has(a.audiologist_id) ? a.audiologist_id : '__unassigned__';
      m.get(key).push(a);
    });
    return m;
  }, [view, appointments, audios]);

  if (view === 'agenda') {
    return (
      <div
        className="flex-1 min-h-0 overflow-auto hc-scrollbar bg-white p-4"
        data-testid={CAL.canvas}
      >
        {appointments.length === 0 ? (
          <div className="text-center py-16 text-stone-500 text-sm">
            {loading ? 'Loading…' : 'No appointments in this range.'}
          </div>
        ) : (
          <div className="space-y-1">
            {appointments.map((apt) => (
              <div
                key={apt.id}
                onClick={() => onSelectAppointment(apt.id)}
                data-testid={CAL.appointmentChip(apt.id)}
                className="border border-stone-200 rounded-md p-3 hover:bg-stone-50 cursor-pointer flex items-center gap-3"
              >
                <div className="w-24 text-xs text-stone-600 tabular-nums">
                  <div className="font-semibold text-stone-800">
                    {new Date(apt.start_ts).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                  </div>
                  <div>{new Date(apt.start_ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-stone-900">
                    {lookups.patient[apt.patient_id]?.full_name || apt.patient_name || 'Patient'}
                  </div>
                  <div className="text-xs text-stone-600">
                    {lookups.aptType[apt.appointment_type_id]?.name || '—'} ·{' '}
                    {lookups.employee[apt.audiologist_id]?.full_name || 'Unassigned'} ·{' '}
                    {lookups.clinic[apt.clinic_id]?.name || '—'}
                  </div>
                </div>
                <div>
                  <AppointmentChip
                    apt={apt}
                    patient={lookups.patient[apt.patient_id]}
                    aptType={lookups.aptType[apt.appointment_type_id]}
                    employee={lookups.employee[apt.audiologist_id]}
                    onClick={onSelectAppointment}
                    compact
                    draggable={false}
                    style={{ width: 180 }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (view === 'resource') {
    // Day resource lanes: columns = audiologists for cursor date
    const dayStart = startOfDay(cursor);
    return (
      <div
        className="flex-1 min-h-0 overflow-auto hc-scrollbar bg-white flex items-start"
        data-testid={CAL.canvas}
      >
        <TimeGutter />
        <div className="flex flex-1 min-w-0">
          {audios.map((emp) => (
            <DayColumn
              key={emp.id}
              date={dayStart}
              columnKey={emp.id}
              header={emp.full_name.replace('Dr. ', '')}
              colorTag={emp.color}
              appointments={(perAudio?.get(emp.id) || []).filter((a) => sameDay(a.start_ts, dayStart))}
              lookups={lookups}
              onSelect={onSelectAppointment}
              onDropAppointment={(id, s, e) => {
                // Also reassign audiologist
                onAppointmentMoved && onAppointmentMoved(id, s, e, { audiologist_id: emp.id });
              }}
            />
          ))}
          {(perAudio?.get('__unassigned__') || []).filter((a) => sameDay(a.start_ts, dayStart)).length > 0 && (
            <DayColumn
              date={dayStart}
              columnKey="unassigned"
              header="Unassigned"
              appointments={(perAudio?.get('__unassigned__') || []).filter((a) => sameDay(a.start_ts, dayStart))}
              lookups={lookups}
              onSelect={onSelectAppointment}
              onDropAppointment={onAppointmentMoved}
            />
          )}
        </div>
      </div>
    );
  }

  // day + week
  return (
    <div
      className="flex-1 min-h-0 overflow-auto hc-scrollbar bg-white flex items-start"
      data-testid={CAL.canvas}
    >
      <TimeGutter />
      <div className="flex flex-1 min-w-0">
        {days.map((d) => (
          <DayColumn
            key={d.toISOString()}
            date={d}
            columnKey={d.toISOString().slice(0, 10)}
            header={d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
            appointments={perDay.get(d.toDateString()) || []}
            lookups={lookups}
            onSelect={onSelectAppointment}
            onDropAppointment={onAppointmentMoved}
          />
        ))}
      </div>
    </div>
  );
}
